import React, { useState, useEffect, useRef, useCallback } from "react";

import {
  getCurrentStyleTarget,
  runEditorCommand
} from "@/helpers/editor/command-style";

import ToolbarButton from "./toolbar-button";

interface Props {
  editorRef: React.RefObject<HTMLDivElement>;
}

const FontPicker: React.FC<Props> = ({ editorRef }) => {
  const [selectedFont, setSelectedFont] = useState<string>("");
  const savedRangeRef = useRef<Range | null>(null);

  const getSelectedTextFont = useCallback(() => {
    const styleTarget = getCurrentStyleTarget(editorRef);
    if (styleTarget) {
      const fontFamily = window.getComputedStyle(styleTarget).fontFamily;
      return fontFamily.replace(/"/g, "");
    }

    return "";
  }, [editorRef]);

  const handleFontChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const font = event.target.value;
    setSelectedFont(font);

    if (editorRef.current) {
      // Try to restore previously saved selection (if any) so the
      // execCommand applies to the expected range even though the
      // select briefly blurs the editor.
      const selection = window.getSelection();
      if (savedRangeRef.current) {
        try {
          selection?.removeAllRanges();
          selection?.addRange(savedRangeRef.current);
        } catch {
          // ignore if restoring fails
        }
      }

      runEditorCommand(editorRef, "fontName", font);

      // Clear saved range after applying
      savedRangeRef.current = null;
    }
  };

  const updateSelectedTextFont = useCallback(() => {
    const font = getSelectedTextFont();
    setSelectedFont(font);
  }, [getSelectedTextFont]);

  useEffect(() => {
    const editorElement = editorRef.current;

    if (editorElement) {
      editorElement.addEventListener("mouseup", updateSelectedTextFont);
      editorElement.addEventListener("keyup", updateSelectedTextFont);
    }

    return () => {
      if (editorElement) {
        editorElement.removeEventListener("mouseup", updateSelectedTextFont);
        editorElement.removeEventListener("keyup", updateSelectedTextFont);
      }
    };
  }, [editorRef, updateSelectedTextFont]);

  return (
    <ToolbarButton className="size-fit">
      <select
        value={selectedFont}
        onChange={handleFontChange}
        // Save the current selection on mousedown so we can restore it
        // before applying commands. We DO NOT preventDefault here because
        // that would stop the native select dropdown from opening.
        onMouseDown={() => {
          const selection = window.getSelection();
          if (selection && selection.rangeCount > 0) {
            // cloneRange to keep a stable copy even if the DOM focus changes
            savedRangeRef.current = selection.getRangeAt(0).cloneRange();
          }
        }}
        className="rounded border px-2 py-1"
      >
        <option value="">Default</option>
        <option value="Tahoma">Tahoma</option>
        <option value="Arial">Arial</option>
        <option value="Times New Roman">Times New Roman</option>
        <option value="Courier New">Courier New</option>
        <option value="Roboto">Roboto</option>
        <option value="Open Sans">Open Sans</option>
        <option value="Montserrat">Montserrat</option>
        <option value="Lato">Lato</option>
        <option value="Poppins">Poppins</option>
        <option value="Nunito">Nunito</option>
        <option value="Source Sans Pro">Source Sans Pro</option>
        <option value="Calibri">Calibri</option>
        <option value="B Yekan">B Yekan</option>
        <option value="B Nazanin">B Nazanin</option>
        <option value="B Zar">B Zar</option>
        <option value="B Lotus">B Lotus</option>
        <option value="B Mitra">B Mitra</option>
        <option value="B Titr">B Titr</option>
        <option value="B Yagut">B Yagut</option>
        <option value="B Koodak">B Koodak</option>
        <option value="B Badr">B Badr</option>
        <option value="B Traffic">B Traffic</option>
      </select>
    </ToolbarButton>
  );
};

export default FontPicker;
